/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-11 14:28:53
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { Card, Badge, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTooltip } from 'src/components/views/common/render-tooltip';
import { getPlanInfo } from 'src/helpers/subscription-helper';
import { formatNumberInThousand, nowTimestamp } from 'src/helpers';
import { DATE_FORMAT } from 'src/constants/language-setting';

type Props = {
  status: string,
  selectedPlan: string,
  currentTotalProducts: number,
  correctPlan: Object,
  hasExtraFee: boolean
};

type ComposedProps = Props & WithI18nProps;

class PlanStatus extends Component<ComposedProps> {
  renderStatus = () => {
    const { i18n, status, selectedPlan = '', showInformation = false } = this.props;

    let label = '';
    if (showInformation) {
      // Show old plan
      label = i18n.translate(
        _.get(getPlanInfo(selectedPlan), 'label', 'AdminHTML.Subscription.Subscription.plan0')
      );
    } else {
      if (selectedPlan.includes('plan') && selectedPlan !== 'offer0_1') {
        label = i18n.translate('AdminHTML.Subscription.PlanStatus.paid');
      } else {
        // Current plan
        // Subscribe plus plan base on plan
        label = i18n.translate(
          _.get(getPlanInfo(selectedPlan), 'label', 'AdminHTML.Subscription.Subscription.plan0')
        );
      }
    }

    const statusBadge = {
      trial: (
        <Badge status="attention">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Trial')}
          </span>
        </Badge>
      ),
      expired: (
        <span className="badge-dangerous">
          <Badge status="warning">
            <span className="title">
              {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Expired')}
            </span>
          </Badge>
        </span>
      ),
      regularExpired: (
        <span className="badge-dangerous">
          <Badge status="warning">
            <span className="title">
              {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Expired')}
            </span>
          </Badge>
        </span>
      ),
      expiredSoon: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_ExpiredSoon')}
          </span>
        </Badge>
      ),
      regularExpiredSoon: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_ExpiredSoon')}
          </span>
        </Badge>
      ),
      subscribed: (
        <Badge status="success">
          <span className="title">{label}</span>
        </Badge>
      ),
      exceededWithNotification: (
        <Badge>
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Exceeded')}
          </span>
        </Badge>
      ),
      exceeded: (
        <Badge>
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Exceeded')}
          </span>
        </Badge>
      ),
      specialDiscountExpiredSoon: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_DiscountExpiring')}
          </span>
        </Badge>
      ),
      specialDiscount2021ExpiredSoon: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_DiscountExpiring')}
          </span>
        </Badge>
      ),
      specialDiscountExpired: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Expired')}
          </span>
        </Badge>
      ),
      passwordProtectedOfferExpiredSoon: (
        <Badge status="attention">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Expiring')}
          </span>
        </Badge>
      ),
      passwordProtectedOfferExpired: (
        <Badge status="warning">
          <span className="title">
            {i18n.translate('AdminHTML.Subscription.PlanStatus.status_Expired')}
          </span>
        </Badge>
      )
    };

    return statusBadge[status];
  };

  renderStatusNotice = () => {
    let {
      i18n,
      status,
      correctPlan,
      dayLeft,
      regularExpiredAt,
      showInformation = false,
      selectedPlan = '',
      suggestDiscountCode
    } = this.props;
    const { label } = correctPlan;

    // Applied discount message
    const appliedDiscountMessage = this.renderNotice_AppliedDiscount();

    const discountMessage =
      suggestDiscountCode === 'BFCM22' ? (
        <span>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.suggestDiscountCode', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.Subscription.PlanStatus.suggestDiscountCode_t1')}
              </span>
            )
          })}
        </span>
      ) : (
        ''
      );

    const statusNotice = {
      trial: (
        <span>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Trial', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Trial_t1', {
                  nt_day_left: dayLeft
                })}
              </span>
            ),
            nt_label: label ? i18n.translate(label) : label
          })}
        </span>
      ),
      expired: (
        <span>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Expired', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Expired_t1')}
              </span>
            ),
            nt_label: label ? i18n.translate(label) : label
          })}
        </span>
      ),
      regularExpired: (
        <span>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_regularPlanExpired_P1', {
            date: <TextStyle variation="strong">{regularExpiredAt}</TextStyle>,
            t1: (
              <span>
                {i18n.translate(
                  `AdminHTML.Subscription.PlanStatus.statusNotice_regularPlanExpired_P2`
                )}
              </span>
            )
          })}
        </span>
      ),
      expiredSoon: (
        <span>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_ExpiredSoon', {
            t1: (
              <span style={{ color: '#de3618' }}>
                {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_ExpiredSoon_t1')}
              </span>
            ),
            nt_label: label ? i18n.translate(label) : label
          })}
        </span>
      ),
      regularExpiredSoon: (
        <span>
          {i18n.translate(
            'AdminHTML.Subscription.PlanStatus.statusNotice_regularPlanExpiredSoon_P1',
            {
              date: <TextStyle variation="strong">{regularExpiredAt}</TextStyle>,
              t1: (
                <span>
                  {i18n.translate(
                    `AdminHTML.Subscription.PlanStatus.statusNotice_regularPlanExpiredSoon_P2`
                  )}
                </span>
              )
            }
          )}
        </span>
      ),
      subscribed:
        selectedPlan === 'offer0_1' ? (
          <span>
            {i18n.translate(
              'AdminHTML.Dashboard.SubscriptionStatus.subscribed_PasswordProtectedOffer_Paragraph'
            )}
          </span>
        ) : selectedPlan.includes('plan') ? (
          discountMessage // Old plan
        ) : (
          <div>
            <p>{i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Subscribed')}</p>
            {appliedDiscountMessage}
          </div>
        ),
      exceededWithNotification: (
        <span style={{ color: '#de3618' }}>
          {i18n.translate(
            'AdminHTML.Subscription.PlanStatus.statusNotice_ExceededWithNotification',
            {
              nt_discount_message: discountMessage
            }
          )}
        </span>
      ),
      exceeded: (
        <span style={{ color: '#de3618' }}>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_Exceeded', {
            nt_discount_message: discountMessage
          })}
        </span>
      ),
      specialDiscount2021ExpiredSoon: this.renderNotice_SpecialDiscountCode_ExpiredSoon('bfcm21'),
      specialDiscountExpiredSoon: this.renderNotice_SpecialDiscountCode_ExpiredSoon(),
      specialDiscountExpired: this.renderNotice_SpecialDiscountCode_Expired(),
      passwordProtectedOfferExpiredSoon: this.renderNotice_PassworProtectedOffer_ExpiredSoon(),
      passwordProtectedOfferExpired: this.renderNotice_PassworProtectedOffer_Expired()
    };

    if (showInformation) {
      return <span>{i18n.translate('AdminHTML.Subscription.PlanStatus.rightPlan')}</span>;
    }
    return statusNotice[status];
  };

  render() {
    const { i18n, status, currentTotalProducts = 0, hasExtraFee, tenantApp = {} } = this.props;
    const { appliedDiscountCode = {} } = tenantApp;
    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    const chargedDiscountPercent = _.get(appliedDiscountCode, 'percent', 0) * 100;

    // Expired date of discount duration
    const expiredAt = _.get(appliedDiscountCode, 'expiredAt', 0);

    const planStatus = i18n.translate(
      `AdminHTML.Subscription.PlanStatus.${
        status === 'subscribed' ? 'currentPlan' : 'currentStatus'
      }`
    );

    return (
      <div className="planStatus">
        <Card sectioned>
          <div className="top">
            <div className="status">
              <div>{planStatus}</div>
              <div>
                {this.renderStatus()}

                {/* Render extra fee badge */}
                {hasExtraFee && (
                  <span style={{ marginLeft: '10px' }}>
                    <Badge status="critical">
                      <span className="title">
                        {i18n.translate('AdminHTML.Subscription.PlanStatus.addon')}
                      </span>
                    </Badge>
                  </span>
                )}

                {/* Render discount percent badge */}
                {chargedDiscountPercent && (expiredAt > today || !expiredAt) ? (
                  <span style={{ marginLeft: '10px' }}>
                    <Badge status="info">
                      <span className="title">
                        {i18n.translate('AdminHTML.Subscription.PlanStatus.discountPercentBadge', {
                          nt_percent: chargedDiscountPercent
                        })}
                      </span>
                    </Badge>
                  </span>
                ) : (
                  ''
                )}
              </div>
            </div>

            <div className="productCount">
              <div>{i18n.translate('AdminHTML.Subscription.PlanStatus.currentProductCount')}</div>
              <div className="count">{formatNumberInThousand(currentTotalProducts)}</div>
            </div>
          </div>

          <div className="bottom">{this.renderStatusNotice()}</div>
        </Card>
      </div>
    );
  }

  renderNotice_SpecialDiscountCode_ExpiredSoon = status => {
    const { i18n, tenantApp = {} } = this.props;
    const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', nowTimestamp());
    const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

    // Convert time to string
    const formatDateNumber = DATE_FORMAT[appLanguage].longDate;
    const expiredDate = moment(specialDiscountExpiredAt * 1000).format(formatDateNumber);

    return (
      <React.Fragment>
        <span style={{ color: '#916A00' }}>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_DiscountExpiredSoon_P1', {
            t1: <TextStyle variation="strong">{expiredDate}</TextStyle>
          })}
        </span>{' '}
        {status === 'bfcm21' ? (
          <TextStyle>
            {i18n.translate(
              'AdminHTML.Subscription.PlanStatus.statusNotice_passwordProtectedOffer_ExpiredSoon_P2',
              {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
                  </TextStyle>
                )
              }
            )}
          </TextStyle>
        ) : (
          <TextStyle>
            {i18n.translate(
              'AdminHTML.Subscription.PlanStatus.statusNotice_DiscountExpiredSoon_P2',
              {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
                  </TextStyle>
                )
              }
            )}
          </TextStyle>
        )}
      </React.Fragment>
    );
  };

  renderNotice_SpecialDiscountCode_Expired = () => {
    const { i18n, tenantApp = {} } = this.props;

    const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', nowTimestamp());
    const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

    // Convert time to string
    const formatDateNumber = DATE_FORMAT[appLanguage].longDate;
    const expiredDate = moment(specialDiscountExpiredAt * 1000).format(formatDateNumber);

    return (
      <React.Fragment>
        <TextStyle variation="negative">
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_DiscountExpired_P1', {
            t1: <TextStyle variation="strong">{expiredDate}</TextStyle>
          })}
        </TextStyle>{' '}
        <TextStyle>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_DiscountExpired_P2', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
              </TextStyle>
            )
          })}
        </TextStyle>
      </React.Fragment>
    );
  };

  renderNotice_PassworProtectedOffer_ExpiredSoon = () => {
    const { i18n, dayLeft } = this.props;

    // Uppercase first character (fix for german text, since duration appears at the beginning of string)
    let duration = i18n.translate(`AdminHTML.Common.Duration.dayV2`, {
      count: dayLeft,
      nt_number: i18n.formatNumber(dayLeft)
    });
    duration = _.upperFirst(duration);

    return (
      <React.Fragment>
        <span style={{ color: '#916A00' }}>
          {i18n.translate(
            'AdminHTML.Subscription.PlanStatus.statusNotice_passwordProtectedOffer_ExpiredSoon_P1',
            {
              nt_duration_in_day: <TextStyle variation="strong">{duration}</TextStyle>
            }
          )}
        </span>{' '}
        <TextStyle>
          {i18n.translate(
            'AdminHTML.Subscription.PlanStatus.statusNotice_passwordProtectedOffer_ExpiredSoon_P2',
            {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
                </TextStyle>
              )
            }
          )}
        </TextStyle>
      </React.Fragment>
    );
  };

  renderNotice_PassworProtectedOffer_Expired = () => {
    const { i18n } = this.props;
    return (
      <React.Fragment>
        <TextStyle>
          {i18n.translate(
            'AdminHTML.Subscription.PlanStatus.statusNotice_passwordProtectedOffer_Expired'
          )}
        </TextStyle>{' '}
        <TextStyle>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_DiscountExpired_P2', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Common.Subscription.reSubscribe`)}
              </TextStyle>
            )
          })}
        </TextStyle>
      </React.Fragment>
    );
  };

  renderNotice_AppliedDiscount = () => {
    const { tenantApp, i18n } = this.props;

    const appliedDiscountCode = _.get(tenantApp, 'appliedDiscountCode', {});
    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    // Expired date of discount duration
    const expiredAt = _.get(appliedDiscountCode, 'expiredAt', 0);

    let appliedDiscountMessage = '';

    if (!_.isEmpty(appliedDiscountCode) && expiredAt > today) {
      const percent = _.get(appliedDiscountCode, 'percent', 0) * 100;
      const duration = appliedDiscountCode.duration
        ? appliedDiscountCode.duration > 1
          ? i18n.translate('AdminHTML.Subscription.PlanStatus.discountDurationPlural', {
              nt_duration: appliedDiscountCode.duration
            })
          : i18n.translate('AdminHTML.Subscription.PlanStatus.discountDuration', {
              nt_duration: appliedDiscountCode.duration
            })
        : i18n.translate('AdminHTML.Subscription.PlanStatus.discountLifetime');
      const startAt = _.get(appliedDiscountCode, 'startAt', 0);
      const startFromDate = moment(startAt * 1000).format('MMM DD, YYYY');

      appliedDiscountMessage = (
        <p style={{ marginTop: '3px' }}>
          {i18n.translate('AdminHTML.Subscription.PlanStatus.statusNotice_AppliedDiscount', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.Subscription.PlanStatus.discountPercentBadge', {
                  nt_percent: percent
                })}
              </TextStyle>
            ),
            t2: <TextStyle variation="strong">{duration}</TextStyle>,
            t3: <TextStyle variation="strong">{startFromDate}</TextStyle>
          })}
          <RenderTooltip
            content={i18n.translate(
              'AdminHTML.Subscription.PlanStatus.statusNotice_AppliedDiscount_Tooltip'
            )}
            icon={true}
          />
        </p>
      );
    }

    return appliedDiscountMessage;
  };
}

export default withI18n()(PlanStatus);
